import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { getRequiresFallback } from '../../DaEditorWrapper/redux/selectors';
import { getIsFocusRequested, getPublishResult, isBusy as isBusySelector, } from '../redux/selectors';
import { CommentEditorSource } from '../redux/types';
import { useCommentEditorError } from './useCommentEditorError';
import { useEditorDraftUnmountEffects } from './useEditorDraftUnmountEffects';
import { usePublishSuccessCleanup } from './usePublishSuccessCleanup';
import { useEditorDraftState } from './useEditorDraftState';
import { useForceLoadIfNeeded } from './useForceLoadIfNeeded';
/** Provides all the state and effects you'll need for a Comment editor! */
export function useCommentEditor(commentBoxId, commentableItemId, commentableType, _a) {
    var _b = _a === void 0 ? {
        editorSource: CommentEditorSource.Independent,
    } : _a, editorSource = _b.editorSource, relevantComment = _b.relevantComment, autoFocus = _b.autoFocus, initiallyExpanded = _b.initiallyExpanded, onPublishSuccess = _b.onPublishSuccess;
    var logBiEvent = useBiLogger();
    var isFocusRequested = useSelector(partialRight(getIsFocusRequested, commentBoxId, autoFocus));
    var isBusy = useSelector(partialRight(isBusySelector, commentBoxId));
    var shouldUseFallback = useSelector(getRequiresFallback);
    var _c = useState(true), isFallbackEmpty = _c[0], setIsFallbackEmpty = _c[1];
    var _d = useState(initiallyExpanded ||
        isFocusRequested ||
        editorSource === CommentEditorSource.Edit), isExpanded = _d[0], setIsExpanded = _d[1];
    var editorDraftState = useEditorDraftState(commentBoxId);
    var expandEditor = useCallback(function () {
        logBiEvent({
            evid: BiEventType.CLICK_ON_COMMENTS_TO_START_TYPING,
            itemid: typeof commentableItemId === 'string'
                ? parseInt(commentableItemId)
                : commentableItemId,
            typeid: commentableType,
        });
        setIsExpanded(true);
    }, [logBiEvent, commentableItemId, commentableType]);
    var collapseEditor = useCallback(function () { return setIsExpanded(false); }, []);
    useCommentEditorEffects({
        editorId: commentBoxId,
        isFocusRequested: isFocusRequested,
        expandEditor: expandEditor,
        collapseEditor: collapseEditor,
        onPublishSuccess: onPublishSuccess,
        editorSource: editorSource,
        hasInitialContent: editorSource === CommentEditorSource.Edit || !!editorDraftState,
    });
    var initialContent = null;
    if (editorDraftState) {
        initialContent = editorDraftState;
    }
    else if (editorSource === CommentEditorSource.Edit && relevantComment) {
        initialContent = relevantComment.textContent;
    }
    return {
        editorSource: editorSource,
        isBusy: isBusy,
        shouldUseFallback: shouldUseFallback,
        isFallbackEmpty: isFallbackEmpty,
        setIsFallbackEmpty: setIsFallbackEmpty,
        relevantComment: relevantComment,
        isFocusRequested: isFocusRequested,
        isExpanded: isExpanded,
        expandEditor: expandEditor,
        collapseEditor: collapseEditor,
        initialContent: initialContent,
    };
}
/**
 * Handles all the effects that a comment editor needs to handle.
 *
 * Some of what it handles:
 * - Draft saving/deleting
 * - Error handling
 * - Expand on focus
 * - Collapse on publish
 * - and more!
 */
function useCommentEditorEffects(_a) {
    var editorId = _a.editorId, isFocusRequested = _a.isFocusRequested, collapseEditor = _a.collapseEditor, expandEditor = _a.expandEditor, onPublishSuccess = _a.onPublishSuccess, editorSource = _a.editorSource, hasInitialContent = _a.hasInitialContent;
    useEditorDraftUnmountEffects(editorId);
    useCommentEditorError(editorId);
    var publishResult = useSelector(partialRight(getPublishResult, editorId));
    // Make sure we trigger onPublishSuccess if publishResult comes back successful
    // TODO: Run this in a saga somewhere instead?
    usePublishSuccessCleanup(editorId, publishResult, onPublishSuccess);
    useEffect(function () {
        if (publishResult) {
            collapseEditor();
        }
    }, [publishResult, collapseEditor]);
    useEffect(function () {
        if (isFocusRequested) {
            expandEditor();
        }
    }, [isFocusRequested, expandEditor]);
    useForceLoadIfNeeded(editorSource, isFocusRequested, hasInitialContent);
}
//# sourceMappingURL=useCommentEditor.js.map